import { useEffect, useRef, useState } from "react";
import AdminLayout, { KPICard, StatusBadge } from "../../components/admin/AdminLayout";
import { Users, UserCheck, UserX, Plus, Upload, Download, Trash2, X, Search } from "lucide-react";
import {
    listSubscribers,
    subscribersStats,
    createSubscriber,
    deleteSubscriber,
    uploadSubscribersCsv,
    downloadSubscribersCsv,
    SITIO_BADGE,
} from "../../lib/admin-api";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const PAGE_SIZE = 25;

const SITIOS = [
    { value: "", label: "Todos" },
    { value: "eclipse", label: "Eclipse" },
    { value: "flexshade", label: "FlexShade" },
    { value: "velarias", label: "Velarias" },
];

export default function AdminSubscribers() {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [q, setQ] = useState("");
    const [sitio, setSitio] = useState("");
    const [unsubFilter, setUnsubFilter] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const params = { limit: PAGE_SIZE, skip, q };
                if (sitio) params.sitio = sitio;
                if (unsubFilter !== "") params.unsubscribed = unsubFilter === "true";
                const [s, list] = await Promise.all([subscribersStats(), listSubscribers(params)]);
                if (!alive) return;
                setStats(s);
                setItems(list.items || []);
                setTotal(list.total || 0);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; };
    }, [skip, q, sitio, unsubFilter, refreshTrigger]);

    const reload = () => setRefreshTrigger((x) => x + 1);

    const handleDelete = async (sub) => {
        if (!window.confirm(`¿Eliminar suscriptor ${sub.email}?`)) return;
        try {
            await deleteSubscriber(sub.id);
            toast.success("Suscriptor eliminado");
            reload();
        } catch (e) {
            toast.error("Error al eliminar");
        }
    };

    return (
        <AdminLayout title="Suscriptores" subtitle="Lista de correos para campañas y newsletters" testid="admin-subscribers-page">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard label="Total" value={stats?.total ?? "—"} icon={Users} accent="text-eclipse-cta" testid="subs-kpi-total" />
                <KPICard label="Activos" value={stats?.active ?? "—"} icon={UserCheck} accent="text-green-600" testid="subs-kpi-active" />
                <KPICard label="Dados de baja" value={stats?.unsubscribed ?? "—"} icon={UserX} accent="text-red-600" testid="subs-kpi-unsub" />
                <KPICard label="Nuevos 30d" value={stats?.new_30d ?? "—"} icon={Plus} accent="text-blue-600" testid="subs-kpi-new30" />
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-4 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[11px] text-neutral-500 uppercase tracking-wider">Buscar</label>
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <Input data-testid="subs-search" value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="email, nombre, ciudad" className="pl-9" />
                    </div>
                </div>
                <div>
                    <label className="text-[11px] text-neutral-500 uppercase tracking-wider block mb-1">Sitio</label>
                    <select data-testid="subs-filter-sitio" value={sitio} onChange={(e) => { setSkip(0); setSitio(e.target.value); }} className="h-9 border border-neutral-200 rounded-md px-3 text-sm bg-white">
                        {SITIOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[11px] text-neutral-500 uppercase tracking-wider block mb-1">Estado</label>
                    <select data-testid="subs-filter-unsub" value={unsubFilter} onChange={(e) => { setSkip(0); setUnsubFilter(e.target.value); }} className="h-9 border border-neutral-200 rounded-md px-3 text-sm bg-white">
                        <option value="">Todos</option>
                        <option value="false">Activos</option>
                        <option value="true">Dados de baja</option>
                    </select>
                </div>
                <div className="flex gap-2 ml-auto">
                    <button onClick={() => setShowImport(true)} className="h-9 px-3 border border-neutral-200 rounded-md text-sm flex items-center gap-2 hover:bg-neutral-50" data-testid="subs-import-btn"><Upload className="w-4 h-4" /> Importar CSV</button>
                    <button onClick={() => downloadSubscribersCsv()} className="h-9 px-3 border border-neutral-200 rounded-md text-sm flex items-center gap-2 hover:bg-neutral-50" data-testid="subs-export-btn"><Download className="w-4 h-4" /> Exportar</button>
                    <button onClick={() => setShowCreate(true)} className="h-9 px-4 bg-eclipse-cta text-white text-sm font-semibold rounded-md flex items-center gap-2 hover:bg-orange-600" data-testid="subs-create-btn"><Plus className="w-4 h-4" /> Agregar</button>
                </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Fecha</th>
                                <th className="text-left px-4 py-3">Email</th>
                                <th className="text-left px-4 py-3">Nombre</th>
                                <th className="text-left px-4 py-3">Ciudad</th>
                                <th className="text-left px-4 py-3">Sitio</th>
                                <th className="text-left px-4 py-3">Fuente</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-right px-4 py-3">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-neutral-400">Sin suscriptores</td></tr>}
                            {items.map((s) => {
                                const badge = SITIO_BADGE[s.sitio] || { label: s.sitio, color: "bg-neutral-200" };
                                return (
                                    <tr key={s.id} className="border-t border-neutral-100 hover:bg-neutral-50/40" data-testid={`sub-row-${s.id}`}>
                                        <td className="px-4 py-2.5 text-neutral-500 whitespace-nowrap">{new Date(s.created_at).toLocaleDateString("es-MX")}</td>
                                        <td className="px-4 py-2.5 font-medium text-neutral-900">{s.email}</td>
                                        <td className="px-4 py-2.5 text-neutral-700">{s.nombre || "—"}</td>
                                        <td className="px-4 py-2.5 text-neutral-500">{s.ciudad || "—"}</td>
                                        <td className="px-4 py-2.5"><StatusBadge label={badge.label} color={badge.color} /></td>
                                        <td className="px-4 py-2.5 text-neutral-500 text-xs">{s.fuente || "—"}</td>
                                        <td className="px-4 py-2.5">{s.unsubscribed ? <StatusBadge label="Baja" color="bg-red-100 text-red-700" /> : <StatusBadge label="Activo" color="bg-green-100 text-green-700" />}</td>
                                        <td className="px-4 py-2.5 text-right">
                                            <button onClick={() => handleDelete(s)} className="p-1.5 hover:text-red-600 text-neutral-400" title="Eliminar" data-testid={`sub-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100 text-xs text-neutral-500">
                    <div>Mostrando {items.length === 0 ? 0 : skip + 1}-{Math.min(skip + PAGE_SIZE, total)} de {total}</div>
                    <div className="flex gap-2">
                        <button onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} disabled={skip === 0} className="px-3 py-1.5 border border-neutral-200 rounded-md disabled:opacity-40">Anterior</button>
                        <button onClick={() => setSkip(skip + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= total} className="px-3 py-1.5 border border-neutral-200 rounded-md disabled:opacity-40">Siguiente</button>
                    </div>
                </div>
            </div>

            {showCreate && <CreateSubModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />}
            {showImport && <ImportCsvModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); reload(); }} />}
        </AdminLayout>
    );
}

function CreateSubModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ email: "", nombre: "", telefono: "", ciudad: "", sitio: "eclipse", fuente: "manual" });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await createSubscriber(form);
            toast.success("Suscriptor agregado");
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al crear");
        } finally {
            setSaving(false);
        }
    };
    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} className="bg-white max-w-md w-full rounded-md p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="sub-create-modal">
                <div className="flex items-center justify-between"><h3 className="text-lg font-bold">Nuevo suscriptor</h3><button type="button" onClick={onClose}><X className="w-4 h-4" /></button></div>
                <div><label className="text-xs text-neutral-500">Email *</label><Input required type="email" value={form.email} onChange={upd("email")} data-testid="sub-create-email" /></div>
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-neutral-500">Nombre</label><Input value={form.nombre} onChange={upd("nombre")} data-testid="sub-create-nombre" /></div>
                    <div><label className="text-xs text-neutral-500">Teléfono</label><Input value={form.telefono} onChange={upd("telefono")} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-neutral-500">Ciudad</label><Input value={form.ciudad} onChange={upd("ciudad")} /></div>
                    <div><label className="text-xs text-neutral-500">Sitio</label>
                        <select value={form.sitio} onChange={upd("sitio")} className="w-full h-9 border border-neutral-200 rounded-md px-3 text-sm bg-white">
                            <option value="eclipse">Eclipse</option><option value="flexshade">FlexShade</option><option value="velarias">Velarias</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-md">Cancelar</button>
                    <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-eclipse-cta text-white rounded-md font-semibold disabled:opacity-50" data-testid="sub-create-submit">{saving ? "Guardando…" : "Guardar"}</button>
                </div>
            </form>
        </div>
    );
}

function ImportCsvModal({ onClose, onDone }) {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const res = await uploadSubscribersCsv(file);
            setResult(res);
            toast.success(`Importados: ${res.imported}, omitidos: ${res.skipped}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al importar");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white max-w-md w-full rounded-md p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="sub-import-modal">
                <div className="flex items-center justify-between"><h3 className="text-lg font-bold">Importar CSV</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
                <p className="text-xs text-neutral-500">El CSV debe tener columna <code>email</code>. Otras columnas opcionales: nombre, telefono, ciudad, tipo_espacio, fuente, sitio.</p>
                <input ref={fileRef} type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="sub-import-file" />
                {result && (
                    <div className="bg-neutral-50 border border-neutral-200 rounded p-3 text-xs">
                        <div>Importados: <strong className="text-green-700">{result.imported}</strong></div>
                        <div>Omitidos: <strong>{result.skipped}</strong></div>
                        {result.errors?.length > 0 && <div className="mt-2 text-red-600">Errores:<ul className="list-disc ml-4">{result.errors.map((er, i) => <li key={i}>{er}</li>)}</ul></div>}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={result ? onDone : onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-md">{result ? "Cerrar" : "Cancelar"}</button>
                    {!result && <button onClick={submit} disabled={!file || busy} className="px-4 py-2 text-sm bg-eclipse-cta text-white rounded-md font-semibold disabled:opacity-50" data-testid="sub-import-submit">{busy ? "Subiendo…" : "Importar"}</button>}
                </div>
            </div>
        </div>
    );
}
